import { createContext, useContext, ReactNode } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import { useDataPrefetch } from '@/hooks/useDataPrefetch';

interface DataPrefetchContextType {
  isLoading: boolean;
  isComplete: boolean;
  progress: number;
  errors: string[];
  refetch: () => Promise<any>;
}

const DataPrefetchContext = createContext<DataPrefetchContextType | null>(null);

/**
 * Provider que gerencia o pré-carregamento de dados do usuário
 * Deve ser usado após autenticação para garantir navegação rápida
 */
export const DataPrefetchProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuthStore();
  const prefetchState = useDataPrefetch(user?.id || null, !!user);

  return (
    <DataPrefetchContext.Provider value={prefetchState}>
      {children}
    </DataPrefetchContext.Provider>
  );
};

/**
 * Hook para acessar o estado de pré-carregamento de dados
 */
export const useDataPrefetchContext = () => {
  const context = useContext(DataPrefetchContext);
  if (!context) {
    throw new Error('useDataPrefetchContext deve ser usado dentro de DataPrefetchProvider');
  }
  return context;
};

/**
 * Hook opcional para componentes que querem saber se dados estão sendo carregados
 */
export const useDataPrefetchStatus = () => {
  const context = useContext(DataPrefetchContext);
  
  // Se o provider não está disponível, retorna estado padrão
  if (!context) {
    return {
      isLoading: false,
      isComplete: true,
      progress: 100
    };
  }
  
  return {
    isLoading: context.isLoading,
    isComplete: context.isComplete,
    progress: context.progress
  };
};
